import { useCallback, useMemo } from 'react'
import { useQuery } from '@apollo/client'

import { JobsFieldSort, SortOrder } from 'generated/graphql'
import { useDashboardsQueries } from 'hooks-queries/dashboards'

import { GET_RECENTS_DASHBOARDS_LOG } from './consts'
import { GET_RECENTS_OWNED_TOPICS } from './query'

const MAX_RECENTS_OWNED_TOPICS_PER_PAGE = 3

export type Recent =
  | {
      id: string
      type: 'topic'
      name: string
      filters: string
      updatedAt: string
    }
  | {
      id: string
      type: 'dashboard'
      name: string
      updatedAt: string
    }

export function useRecents() {
  const {
    data: ownedTopicsData,
    loading: ownedTopicsLoading,
    error: ownedTopicsError,
    refetch: refetchOwnedTopics,
  } = useQuery(GET_RECENTS_OWNED_TOPICS, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        limit: MAX_RECENTS_OWNED_TOPICS_PER_PAGE,
        offset: 0,
      },
    },
  })

  const {
    data: dashboardsData,
    loading: dashboardsLoading,
    error: dashboardsError,
    refetch: refetchDashboards,
  } = useDashboardsQueries({
    queryName: 'getRecentsDashboards',
    queryVariables: {
      pagination: { take: 3 },
      sort: {
        field: JobsFieldSort.UpdatedAt,
        order: SortOrder.Desc,
      },
    },
    errorLog: GET_RECENTS_DASHBOARDS_LOG,
  })

  const recents = useMemo(() => {
    const recentsOwnedTopicsData = Array.from(ownedTopicsData?.listSavedPostSearchesPaginated?.entities || [])
    const recentsOwnedTopics: Recent[] = recentsOwnedTopicsData?.map((topic: any) => ({
      id: topic.id,
      type: 'topic',
      name: topic.name,
      filters: topic.filters,
      updatedAt: topic.updatedAt,
    }))

    const recentsDashboardsData = Array.from(dashboardsData?.listWorkspaceJobPaginated?.data || [])
    const recentsDashboards: Recent[] = recentsDashboardsData.map((dashboard: any) => ({
      id: dashboard.id,
      type: 'dashboard',
      name: dashboard.name,
      updatedAt: dashboard.updatedAt,
    }))

    const allRecents = [...recentsOwnedTopics, ...recentsDashboards]
    const sortedRecents = allRecents.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())

    return sortedRecents
  }, [dashboardsData?.listWorkspaceJobPaginated?.data, ownedTopicsData?.listSavedPostSearchesPaginated?.entities])

  const isLoading = ownedTopicsLoading || dashboardsLoading
  const error = ownedTopicsError || dashboardsError

  const refetchRecents = useCallback(() => {
    refetchOwnedTopics()
    refetchDashboards()
  }, [refetchDashboards, refetchOwnedTopics])

  const memoizedValues = useMemo(
    () => ({
      recents,
      isLoading,
      error,
      refetchRecents,
    }),
    [recents, isLoading, error, refetchRecents]
  )

  return memoizedValues
}
